"use client";

import React from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowLeft, Home } from "lucide-react";

export default function NotFound() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 overflow-hidden relative">
      {/* Ambient Background Glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-primary/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-700/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-lg">
        {/* Animated 404 Graphic */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mb-8"
        >
          <h1 className="text-[12rem] font-bold leading-none tracking-tighter text-charcoal select-none opacity-50">
            404
          </h1>
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
              }}
              className="w-32 h-32 border border-accent-primary/30 rounded-[2rem] flex items-center justify-center backdrop-blur-sm bg-background/40"
            >
              <div className="w-16 h-16 border-2 border-accent-primary rounded-xl flex items-center justify-center">
                <div className="w-4 h-4 bg-accent-primary rounded-full animate-pulse" />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-3xl font-semibold text-white mb-4 tracking-tight">
            Lost in Space?
          </h2>
          <p className="text-silver-gray text-lg mb-10 leading-relaxed max-w-md">
            The page you are looking for has been moved, deleted, or never existed in the first place.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center w-full">
            <Link
              href="/"
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-accent-primary text-white rounded-xl font-medium hover:bg-accent-blue transition-all active:scale-95 shadow-lg shadow-accent-primary/20"
            >
              <Home size={18} />
              Return Home
            </Link>
            <button
              onClick={() => window.history.back()}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-charcoal text-white rounded-xl font-medium border border-dark-slate hover:bg-onyx transition-all active:scale-95"
            >
              <ArrowLeft size={18} />
              Go Back
            </button>
          </div>
        </motion.div>
      </div>

      {/* Footer Decoration */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-3"
      >
        <div className="h-[1px] w-12 bg-linear-to-r from-transparent to-dark-slate" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 font-medium">
          Assetilo Management
        </span>
        <div className="h-[1px] w-12 bg-linear-to-l from-transparent to-dark-slate" />
      </motion.div>
    </div>
  );
}
